import React, { useState, useEffect, useRef } from 'react';
import { X, Users } from 'lucide-react';

const formatEmployeeLabel = (emp) =>
  emp ? (emp.serviceNo ? `${emp.serviceNo} — ${emp.name}` : emp.name || '') : '';

const EmployeeSearch = ({
  value,
  onSelect,
  employees = [],
  loading = false,
  showAllOption = true,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (value && typeof value === 'object') {
      if (value.isAll || value.name === 'All Employees') {
        setSearchTerm('');
      } else {
        setSearchTerm(formatEmployeeLabel(value));
      }
    } else if (value && typeof value === 'string') {
      setSearchTerm(value);
    } else {
      setSearchTerm('');
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Filter employees by ID / serviceNo / name ─────────────────────
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees.slice(0, 10));
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = employees.filter(
      (emp) =>
        emp.employeeId?.toLowerCase().includes(term) ||
        emp.name?.toLowerCase().includes(term) ||
        emp.serviceNo?.toLowerCase().includes(term)
    );
    setFilteredEmployees(filtered.slice(0, 20));
  }, [searchTerm, employees]);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setSearchTerm(v);
    setIsOpen(true);
    if (!v.trim()) {
      onSelect(null);
    }
  };

  const handleSelectEmployee = (employee) => {
    setSearchTerm(formatEmployeeLabel(employee));
    setIsOpen(false);
    onSelect(employee);
  };

  const handleSelectAll = () => {
    setSearchTerm('');
    setIsOpen(false);
    onSelect({ employeeId: '__ALL__', name: 'All Employees', isAll: true });
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    setIsOpen(true);
    onSelect(null);
  };

  const handleFocus = () => {
    setIsOpen(true);
    if (value) {
      setSearchTerm('');
      onSelect(null);
    }
  };

  const isAllSelected = value?.isAll === true;

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onClick={handleFocus}
          placeholder={isAllSelected ? 'All Employees' : 'Search by service no or name...'}
          className={`w-full h-9 bg-white border rounded-lg text-xs px-2.5 pr-8 outline-none text-gray-700 ${
            isAllSelected
              ? 'border-primary-400 bg-primary-50'
              : 'border-gray-200 focus:border-primary-500'
          }`}
        />
        {(searchTerm || isAllSelected) && (
          <button
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {/* All Employees Option */}
          {showAllOption && !searchTerm.trim() && (
            <div
              onClick={handleSelectAll}
              className={`px-3 py-2 cursor-pointer border-b border-gray-200 flex items-center gap-2 ${
                isAllSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
              }`}
            >
              <Users size={14} className="text-primary-600" />
              <span className="text-xs font-medium text-primary-700">All Employees</span>
            </div>
          )}

          {/* Employee List */}
          {filteredEmployees.map((emp) => (
            <div
              key={emp.serviceNo || emp.employeeId}
              onClick={() => handleSelectEmployee(emp)}
              className={`px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 ${
                value?.employeeId === emp.employeeId ? 'bg-primary-50' : ''
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-[11px] font-mono text-gray-400 w-20 shrink-0">
                  {emp.serviceNo || '—'}
                </span>
                <span className="text-xs text-gray-800 truncate">{emp.name}</span>
              </div>
              {emp.department && (
                <div className="text-[10px] text-gray-400 mt-0.5 pl-[90px] truncate">
                  {emp.department}
                </div>
              )}
            </div>
          ))}

          {filteredEmployees.length === 0 && searchTerm.trim() && (
            <div className="px-3 py-4 text-center text-xs text-gray-400">
              No employees found
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-center text-xs text-gray-500">
          Loading employees...
        </div>
      )}
    </div>
  );
};

export default EmployeeSearch;